import { useEffect, useState } from 'react';
import { Table, message, Card, Space, Button, Modal, Form, Input, Popconfirm } from 'antd';
import baseuri from '../../methods/baseuri';
import { PageHeader } from '../../theme/theme';

interface SolidPayBotConfig {
    _id: string;
    name: string;
    value: string;
    createdAt: string;
    updatedAt: string;
    __v?: number;
}

const SolidPayBot = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<SolidPayBotConfig[]>([]);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingConfig, setEditingConfig] = useState<SolidPayBotConfig | null>(null);
    const [form] = Form.useForm();
    const [submitLoading, setSubmitLoading] = useState(false);

    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [viewingConfig, setViewingConfig] = useState<SolidPayBotConfig | null>(null);
    const [viewLoading, setViewLoading] = useState(false);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const response = await baseuri.get('/admin/solid-pay-bot-configurations');
            const resData = response.data?.data?.itemsList || [];
            setData(Array.isArray(resData) ? resData : [resData]);
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to fetch solid pay bot configurations');
        } finally {
            setLoading(false);
        }
    };

    const showCreateModal = () => {
        setEditingConfig(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record: SolidPayBotConfig) => {
        setEditingConfig(record);
        form.setFieldsValue({ value: record.value, name: record.name });
        setIsModalVisible(true);
    };

    const handleSubmit = async (values: { value: string; name: string }) => {
        setSubmitLoading(true);
        try {
            if (editingConfig) {
                // Update
                await baseuri.put('/admin/solid-pay-bot-configurations', {
                    id: editingConfig._id,
                    value: values.value,
                    name: values.name,
                });
                message.success('Configuration updated successfully');
            } else {
                // Create
                await baseuri.post('/admin/solid-pay-bot-configurations', {
                    value: values.value,
                    name: values.name,
                });
                message.success('Configuration created successfully');
            }
            setIsModalVisible(false);
            form.resetFields();
            fetchConfigs(); // Refresh data
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to process configuration');
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleView = async (id: string) => {
        setIsViewModalVisible(true);
        setViewLoading(true);
        try {
            const response = await baseuri.get('/admin/solid-pay-bot-configurations/single', { params: { id } });
            setViewingConfig(response.data?.data || null);
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to fetch configuration details');
        } finally {
            setViewLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            await baseuri.delete(`/admin/solid-pay-bot-configurations?id=${id}`);
            message.success('Configuration deleted successfully');
            fetchConfigs();
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to delete configuration');
        }
    };

    useEffect(() => {
        fetchConfigs();
    }, []);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text: string) => text ? new Date(text).toLocaleString() : 'N/A',
        },
        {
            title: 'Updated At',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            render: (text: string) => text ? new Date(text).toLocaleString() : 'N/A',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: SolidPayBotConfig) => (
                <Space size="middle">
                    <Button size="small" onClick={() => handleView(record._id)}>
                        View
                    </Button>
                    <Button type="primary" size="small" onClick={() => handleEdit(record)}>
                        Edit
                    </Button>
                    <Popconfirm
                        title="Are you sure you want to delete this configuration?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger size="small">Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <section>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <PageHeader>Solid Pay Bot Configurations</PageHeader>
                <Button type="primary" onClick={showCreateModal}>
                    Create Configuration
                </Button>
            </div>
            <Card>
                <Table 
                    dataSource={data} 
                    columns={columns} 
                    loading={loading}
                    rowKey={(record) => record._id || record.name || Math.random().toString()} 
                />
            </Card>

            <Modal
                title={`${editingConfig ? 'Edit' : 'Create'} Configuration`}
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                    setEditingConfig(null);
                }}
                onOk={() => form.submit()}
                confirmLoading={submitLoading}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        label="Configuration Name"
                        rules={[{ required: true, message: 'Please input the configuration name!' }]}
                    >
                        <Input placeholder="Enter name" disabled={!!editingConfig} />
                    </Form.Item>
                    <Form.Item
                        name="value"
                        label="Configuration Value"
                        rules={[{ required: true, message: 'Please input the configuration value!' }]}
                    >
                        <Input placeholder="Enter value" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Configuration Details"
                open={isViewModalVisible}
                onCancel={() => {
                    setIsViewModalVisible(false);
                    setViewingConfig(null);
                }}
                footer={[
                    <Button key="close" onClick={() => { setIsViewModalVisible(false); setViewingConfig(null); }}>
                        Close
                    </Button>
                ]}
            >
                {viewLoading ? <p>Loading details...</p> : viewingConfig ? (
                    <div>
                        <p><strong>ID:</strong> {viewingConfig._id}</p>
                        <p><strong>Name:</strong> {viewingConfig.name}</p>
                        <p><strong>Value:</strong> {viewingConfig.value}</p>
                        <p><strong>Created At:</strong> {viewingConfig.createdAt ? new Date(viewingConfig.createdAt).toLocaleString() : 'N/A'}</p>
                        <p><strong>Updated At:</strong> {viewingConfig.updatedAt ? new Date(viewingConfig.updatedAt).toLocaleString() : 'N/A'}</p>
                    </div>
                ) : <p>No details found.</p>}
            </Modal>
        </section>
    );
};

export default SolidPayBot;
